import { CloseButton, Flex, HTMLChakraProps } from '@chakra-ui/react';
import { rest } from 'lodash';
import dynamic from 'next/dynamic';
import { FC, useEffect } from 'react';

import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import { queryThinLectureWithTagAndEvent } from '@/fetchers/thinLectures';
import { useLibLynx } from '@/hooks/useLibLynx';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useResource, useResources } from '@/lib/datx-jsonapi-react';
import { queryLiblynxAuthorizationGrants } from '@/queries/liblynx-authorization-grants';
import { queryLecturePosterOccurrences } from '@/queries/omnisearch-occurrences';
import { FoundIn } from '@/resources/OmnisearchOccurrence';
import { ILibLynxMetadata } from '@/types/LibLynx';

const PdfViewer = dynamic(() => import('@/components/ui/shared/pdf/PdfViewer/PdfViewer').then((mod) => mod.PdfViewer), {
  ssr: false,
});

export const LecturePoster: FC<HTMLChakraProps<'div'>> = () => {
  const { data: liblynxData } = useResource(queryLiblynxAuthorizationGrants('auth_grant'));
  const libLynx = useLibLynx();
  const { id: lectureId } = useLecturePageQueryParams();
  const { data: lecture } = useResource(queryThinLectureWithTagAndEvent(lectureId));
  const { query, index } = useQueryParams<{ query: string; index }>({
    query: StringParam,
    index: StringParam,
  });

  const { data = [] } = useResources(queryLecturePosterOccurrences(lectureId, query));

  useEffect(() => {
    if (!liblynxData) return;
    const libLynxMetadata: ILibLynxMetadata = {
      dtype: 'multimedia',
      id: lecture.doi || lectureId,
      title: `${lecture.title} - [pdf preview]`,
    };

    libLynx.send('trackItemRequest', { im: libLynxMetadata });
  }, [lecture, liblynxData]);

  const posterDocuments = data.find((d) => d.foundIn === FoundIn.POSTER_DOCUMENT);

  return (
    <PdfViewer
      highlightedColor={lecture.highlightedColor}
      occurrenceColor={lecture.occurrenceMatchColor}
      occurrences={posterDocuments?.details}
      highlightedIndex={parseInt(index)}
      src={lecture?.posterDocument?.url}
      query={query}
      w="full"
      h="full"
      flexGrow={1}
    />
  );
};

export const LecturePosterPanel: FC<HTMLChakraProps<'div'>> = ({ children }) => {
  const { posterExpanded, change } = useQueryParams<{ posterExpanded: string }>({
    posterExpanded: StringParam,
  });

  if (posterExpanded === 'false' || !posterExpanded) {
    return null;
  }

  function closePoster() {
    change({ posterExpanded: null }, { method: 'replace', shallow: true });
  }

  return (
    <Flex pos="absolute" top="0" bottom="0" right="0" left="0" bg="black" zIndex="10" direction="column" {...rest}>
      <Flex align="center" justifyContent="flex-end" flexGrow={0}>
        <CloseButton ml="4" onClick={closePoster} />
      </Flex>
      <Flex flex="1">{children}</Flex>
    </Flex>
  );
};
